import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import SearchBox from "../search-box/SearchBox";
import AgreementListing from "../agreement-listing/AgreementListing";
import { agreementApiCall } from "../redux/apiCalls";
import { startFetchingAgreementsAttachFromIci } from "../redux/slice";
import { updateAgreementData } from "../utils";
import { TEXT } from "../../../shared/constants/texts";
import { PAGE, PAGE_SIZE } from "../../../shared/constants/paginationConstants";
import { ATTRIBUTE_DATA_TYPES } from "../../../shared/constants/attributeDataTypes";
import { logError } from "../../../shared/services/errorLogging";

const Listing: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const { selectedSavedSearch, agreementData, attributeData, selectedAttributes } = state;
  const [agreements, setAgreements] = React.useState([]);
  const [hasMoreAgreements, setHasMoreAgreements] = React.useState(true);
  const [skip, setSkip] = React.useState(0);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const searchAgreementRef = React.useRef(null);

  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (selectedSavedSearch) {
      setAgreements([]);
      setHasMoreAgreements(true);
      setSkip(0);
      if (searchAgreementRef && searchAgreementRef.current && searchAgreementRef.current.value) {
        searchAgreementRef.current.value = "";
      }
      getAgreements();
    }
  }, [selectedSavedSearch]);

  React.useEffect(() => {
    if (agreementData && agreementData.rows && agreementData.rows.length > 0) {
      setAgreements(agreementData.rows);
    } else {
      setAgreements([]);
    }
  }, [agreementData]);

  const convertToCamelCase = (str) => {
    return str[0].toLowerCase() + str.slice(1);
  };

  const getAgreements = async (searchTerm = "") => {
    dispatch(startFetchingAgreementsAttachFromIci());
    let attributeDataForSearch = [];
    if (selectedAttributes && selectedAttributes.length > 0) {
      attributeDataForSearch = attributeData.filter((attr) => selectedAttributes.includes(attr.attributeId));
    }
    let requestData = {};
    const commonRequestData = {
      title: selectedSavedSearch.value,
      clientType: "",
      page: PAGE.AGREEMENT_LISTING,
      pageSize: PAGE_SIZE.AGREEMENT_LISTING,
      skip: 0,
    };
    if (searchTerm) {
      if (attributeDataForSearch && attributeDataForSearch.length > 0) {
        let shouldBody = {};
        for (const attribute of attributeDataForSearch) {
          shouldBody = {
            ...shouldBody,
            [attribute.name]: {
              field: convertToCamelCase(attribute.name),
              type: ATTRIBUTE_DATA_TYPES[attribute.dataType],
              isLocalized: attribute.isAttributeLocalised,
              query: [],
              data: [],
              customquery: `*${searchTerm}*`,
              isNot: false,
              op: "",
              textQueryOperator: "contains",
            },
          };
        }
        requestData = {
          ...commonRequestData,
          customData: {
            query: JSON.stringify({ freetext: "", must: {}, must_not: {}, should: shouldBody }),
          },
        };
      } else {
        requestData = {
          ...commonRequestData,
          freetext: `*${searchTerm}*`,
        };
      }
    } else {
      requestData = {
        ...commonRequestData,
      };
    }
    const agreementResponse = await agreementApiCall({
      ...apiCallParams,
      requestData: requestData,
      retryFunctionCall: () => getAgreements(searchTerm),
    });
    const { response, error } = agreementResponse;
    if (response) {
      updateAgreementData(response.data);
    }
    if (error) {
      updateAgreementData(null);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|attach-from-ici|listing|Listing - getAgreements",
          callerApi: "Search/ExecuteCustomizedSavedSearch",
        },
      });
    }
  };

  const getMoreAgreementData = async () => {
    if (agreementData.rows.length >= agreementData.total) {
      setHasMoreAgreements(false);
      return;
    }
    setSkip((prevState) => prevState + PAGE_SIZE.AGREEMENT_LISTING);
    const searchTerm =
      searchAgreementRef && searchAgreementRef.current && searchAgreementRef.current.value
        ? searchAgreementRef.current.value
        : "";
    let attributeDataForSearch = [];
    if (selectedAttributes && selectedAttributes.length > 0) {
      attributeDataForSearch = attributeData.filter((attr) => selectedAttributes.includes(attr.attributeId));
    }
    let requestData = {};
    const commonRequestData = {
      title: selectedSavedSearch.value,
      clientType: "",
      page: PAGE.AGREEMENT_LISTING,
      pageSize: PAGE_SIZE.AGREEMENT_LISTING,
      skip: skip + PAGE_SIZE.AGREEMENT_LISTING,
    };
    if (searchTerm) {
      if (attributeDataForSearch && attributeDataForSearch.length > 0) {
        let shouldBody = {};
        for (const attribute of attributeDataForSearch) {
          shouldBody = {
            ...shouldBody,
            [attribute.name]: {
              field: convertToCamelCase(attribute.displayName),
              type: ATTRIBUTE_DATA_TYPES[attribute.dataType],
              isLocalized: attribute.isAttributeLocalised,
              query: [],
              data: [],
              customquery: `*${searchTerm}*`,
              isNot: false,
              op: "",
              textQueryOperator: "contains",
            },
          };
        }
        requestData = {
          ...commonRequestData,
          customData: {
            query: JSON.stringify({ freetext: "", must: {}, must_not: {}, should: shouldBody }),
          },
        };
      } else {
        requestData = {
          ...commonRequestData,
          freetext: `*${searchTerm}*`,
        };
      }
    } else {
      requestData = {
        ...commonRequestData,
      };
    }
    const agreementResponse = await agreementApiCall({
      ...apiCallParams,
      requestData: requestData,
      retryFunctionCall: () => getMoreAgreementData(),
    });
    const { response, error } = agreementResponse;
    if (response) {
      const newAgreementData = transformAgreementData(response, agreementData);
      updateAgreementData(newAgreementData);
    }
    if (error) {
      updateAgreementData(agreementData);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|attach-from-ici|listing|Listing - getMoreAgreementData",
          callerApi: "Search/ExecuteCustomizedSavedSearch",
        },
      });
    }
  };

  const transformAgreementData = (newResponseData, prevData) => {
    const previousDataRows = [...prevData.rows];
    const data = newResponseData.data;
    let dataRows = [];
    if (data && data.rows && data.rows.length > 0) {
      dataRows = [...data.rows];
    }
    const newRows = previousDataRows.concat(dataRows);
    const newData = {
      rows: newRows,
      rowTotal: newRows.length,
      total: data.total,
    };
    return newData;
  };

  return (
    <React.Fragment>
      <SearchBox searchRef={searchAgreementRef} getAgreementData={getAgreements} />
      <AgreementListing
        agreements={agreements}
        getMoreAgreementData={getMoreAgreementData}
        hasMoreAgreements={hasMoreAgreements}
      />
    </React.Fragment>
  );
};

export default Listing;
